import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Col, Form, Modal, Row, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { AdminInfoForm } from './components/AdminInfoForm/AdminInfoForm';
import { AdminInfoCreateFieldBlock } from './components/AdminInfoCreateFieldBlock/AdminInfoCreateFieldBlock';
import { AdminInfoActions } from './components/AdminInfoActions/AdminInfoActions';
import { useAdminInfoQuery } from './queries/adminInfoQuery';
import { getState } from '../../../../constants/stateMachineStates';
import showErrorMessage from '../../../../helpers/showErrorMessage';
import { setRefreshFn } from '../../../../actions/refreshActions';
import { useNavigationBlocker } from '../../../../hooks/useNavigationBlocker';

const AdministrationInfo = ({ form, companyId, renderToolbar }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((store) => store.authentication.userInfo);
  const userHasAccessToEditCompanyInfo = Boolean(userInfo.CanEditCompanyInfo);

  const [isInfoEditing, setIsInfoEditing] = useState(false);
  const [isInfoSaving, setIsInfoSaving] = useState(false);
  const [isSortEditing, setIsSortEditing] = useState(false);
  const [deleteFieldsQueue, setDeleteFieldsQueue] = useState([]);
  const addDeleteFieldToQueue = (field) =>
    setDeleteFieldsQueue((oldVal) => [...oldVal, field]);
  const removeDeleteFieldFromQueue = (field) =>
    setDeleteFieldsQueue((oldVal) => oldVal.filter((f) => f.id !== field.id));

  const adminInfoQuery = useAdminInfoQuery(
    { companyId },
    {
      onError: (error) => {
        showErrorMessage(
          error,
          'Произошла ошибка при получении информации о компании'
        );
      },
    }
  );

  useEffect(() => {
    dispatch(setRefreshFn(adminInfoQuery.refetch));
    return () => {
      dispatch(setRefreshFn(() => {}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fields, setFields] = useState([]);

  const visibleFields = useMemo(() => {
    return fields.filter((field) =>
      deleteFieldsQueue.every((f) => f.id !== field.id)
    );
  }, [deleteFieldsQueue, fields]);

  useEffect(() => {
    if (adminInfoQuery.data) {
      setFields(adminInfoQuery.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(adminInfoQuery.data)]);

  const cancelSort = () => {
    setFields((oldData) => {
      const oldFieldsMap = new Map(oldData.map((field) => [field.id, field]));
      const getOldOrderNum = (field) => oldFieldsMap.get(field.id).orderNum;
      return fields.map((field) => ({
        ...field,
        orderNum: getOldOrderNum(field),
      }));
    });
  };

  const cancelEdit = () => {
    setDeleteFieldsQueue([]);
    setFields(adminInfoQuery.data || []);
  };

  const newFieldOrder = useMemo(() => {
    const maxOrderNum = Math.max(
      ...(adminInfoQuery?.data?.map((field) => field.orderNum) || [0])
    );
    return maxOrderNum + 1;
  }, [adminInfoQuery.data]);

  useNavigationBlocker(isInfoEditing, ({ location, unblock, history }) => {
    Modal.confirm({
      title: 'Вы уверены, что хотите покинуть страницу?',
      content: 'Введенные вами данные будут потеряны.',
      okText: 'Да',
      cancelText: 'Нет',
      onOk: () => {
        unblock();
        history.push(location);
      },
      onCancel: () => {},
    });
  });

  const actions =
    userHasAccessToEditCompanyInfo && Boolean(companyId) ? (
      <AdminInfoActions
        form={form}
        companyId={companyId}
        initialValues={adminInfoQuery.data || []}
        isEditing={isInfoEditing}
        setIsEditing={setIsInfoEditing}
        isSortEditing={isSortEditing}
        setIsSortEditing={setIsSortEditing}
        isInfoSaving={isInfoSaving}
        setIsInfoSaving={setIsInfoSaving}
        cancelSort={cancelSort}
        cancelEdit={cancelEdit}
        deleteFieldsQueue={deleteFieldsQueue}
        removeDeleteFieldFromQueue={removeDeleteFieldFromQueue}
      />
    ) : null;
  return (
    <>
      <div>
        {renderToolbar({
          actions,
          disableCompanyChange: isInfoEditing,
        })}
      </div>

      <Spin spinning={adminInfoQuery.isFetching || false}>
        <Col
          style={{ padding: '10px' }}
          sm={{
            span: 24,
          }}
        >
          {isInfoEditing && (
            <Row style={{ marginBottom: '20px' }}>
              <Col align="center">
                <Alert
                  type="info"
                  description={
                    <h3>
                      Информация по компании, отраженная в данном модуле, будет
                      доступна собственникам.
                    </h3>
                  }
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col
              sm={{ span: 24 }}
              lg={{ offset: 4, span: 16 }}
            >
              <AdminInfoForm
                form={form}
                readOnly={!isInfoEditing || isInfoSaving || isSortEditing}
                companyId={companyId}
                isSaving={isInfoSaving}
                isSortEditing={isSortEditing}
                fields={visibleFields}
                setFields={setFields}
                state={getState({
                  idle: Boolean(adminInfoQuery?.idle),
                  loading: Boolean(adminInfoQuery?.isLoading),
                  error: Boolean(adminInfoQuery?.error),
                })}
                addDeleteFieldToQueue={addDeleteFieldToQueue}
              />

              {isInfoEditing && !isSortEditing && (
                <AdminInfoCreateFieldBlock
                  companyId={companyId}
                  orderNum={newFieldOrder}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Spin>
    </>
  );
};

AdministrationInfo.propTypes = {
  form: PropTypes.object.isRequired,
  companyId: PropTypes.number.isRequired,
  renderToolbar: PropTypes.func.isRequired,
};

export const AdminInfo = Form.create({
  name: 'administration_info',
})(
  memo(AdministrationInfo, (prevProps, curProps) => {
    const propsForRerender = ['companyId', 'renderToolbar', 'form'];
    return propsForRerender.every(
      (pName) => prevProps[pName] === curProps[pName]
    );
  })
);
